/**
 * Permits fidelity Phase A — required-docs checklist (docs/design/permits-fidelity-ui.md §5,
 * docs/features/permits-fidelity.md §A.4). WARNS, does not block submit — EXCEPT the
 * `affidavit-ack` kind, whose checkbox both lives in this list AND gates submit (the ledger's one
 * exception). `upload` items show "done" once an attachment tile matching that item's key exists (a
 * best-effort match on the tile's status; Phase A doesn't tag uploads per-checklist-key so this is a
 * simple "any file uploaded so far counts toward one upload item" — acceptable per the warn-don't-
 * block posture). `external-prereq` items are a confirmation, not a file.
 */
import type { RequiredDoc } from "../api/types.js";

interface RequiredDocsChecklistProps {
  docs: RequiredDoc[];
  /** Count of attachments already uploaded — drives the "N of M uploaded" summary + which `upload`
   *  rows read as done (Phase A: best-effort by count, not per-key tagging). */
  uploadedCount: number;
  /** affidavit-ack acknowledgment state, keyed by RequiredDoc.key. */
  acks: Record<string, boolean>;
  onAckChange: (key: string, checked: boolean) => void;
  onUploadClick?: () => void;
}

export function RequiredDocsChecklist({
  docs,
  uploadedCount,
  acks,
  onAckChange,
  onUploadClick,
}: RequiredDocsChecklistProps): JSX.Element {
  const uploadDocs = docs.filter((d) => d.kind === "upload");
  const doneUploads = Math.min(uploadedCount, uploadDocs.length);

  return (
    <div>
      <div className="td-checklist__summary">
        <span>You can submit now — staff will follow up on anything still missing.</span>
        {uploadDocs.length ? (
          <span>
            <strong>
              {doneUploads} / {uploadDocs.length}
            </strong>{" "}
            uploaded
          </span>
        ) : null}
      </div>
      <div className="td-checklist" role="list">
        {docs.map((doc) => {
          if (doc.kind === "affidavit-ack") {
            const checked = acks[doc.key] === true;
            return (
              <div key={doc.key} className="td-checklist__item td-checklist__item--required" role="listitem">
                <span className="td-checklist__icon" aria-hidden="true">
                  !
                </span>
                <div className="td-checklist__body">
                  <label className="td-checklist__label" htmlFor={`ack-${doc.key}`}>
                    <input
                      id={`ack-${doc.key}`}
                      type="checkbox"
                      checked={checked}
                      onChange={(e) => onAckChange(doc.key, e.target.checked)}
                    />
                    {doc.label}
                    <span className="td-checklist__tag td-checklist__tag--required">Required to submit</span>
                  </label>
                  {doc.help ? <p className="td-checklist__help">{doc.help}</p> : null}
                </div>
              </div>
            );
          }
          if (doc.kind === "external-prereq") {
            return (
              <div key={doc.key} className="td-checklist__item td-checklist__item--info" role="listitem">
                <span className="td-checklist__icon" aria-hidden="true">
                  i
                </span>
                <div className="td-checklist__body">
                  <div className="td-checklist__label">
                    {doc.label}
                    <span className="td-checklist__tag td-checklist__tag--recommended">Confirm before issue</span>
                  </div>
                  {doc.help ? <p className="td-checklist__help">{doc.help}</p> : null}
                </div>
              </div>
            );
          }
          // "upload"
          const uploadIndex = uploadDocs.findIndex((d) => d.key === doc.key);
          const isDone = uploadIndex > -1 && uploadIndex < doneUploads;
          return (
            <div
              key={doc.key}
              className={`td-checklist__item td-checklist__item--${isDone ? "done" : "needed"}`}
              role="listitem"
            >
              <span className="td-checklist__icon" aria-hidden="true">
                {isDone ? "✓" : "⋮"}
              </span>
              <div className="td-checklist__body">
                <div className="td-checklist__label">
                  {doc.label}
                  <span className="td-checklist__tag td-checklist__tag--recommended">Recommended</span>
                </div>
                <p className="td-checklist__help">
                  {isDone
                    ? "Uploaded."
                    : doc.help ||
                      "Still needed — staff can request this during review if you submit without it."}
                </p>
              </div>
              {!isDone && onUploadClick ? (
                <div className="td-checklist__action">
                  <button type="button" className="td-btn td-btn--secondary td-btn--sm" onClick={onUploadClick}>
                    Upload
                  </button>
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
    </div>
  );
}
